import { CustomWidgetCollection, Question, Serializer } from 'survey-core';
import { attachInfomodalBadgeToPartner } from './info-modal-inline.util';

class InfoModalQuestion extends Question {
  override getType() {
    return 'infomodal';
  }

  get modalTitle() {
    return this.getPropertyValue('modalTitle');
  }
  set modalTitle(value: string) {
    this.setPropertyValue('modalTitle', value);
  }

  get infoText() {
    return this.getPropertyValue('infoText');
  }
  set infoText(value: string) {
    this.setPropertyValue('infoText', value);
  }
}

type InfoModalComponentRef = {
  instance: {
    modalTitle: string;
    infoText: string;
    label: string;
    isOpen: boolean;
  };
  hostView: unknown;
  destroy: () => void;
  changeDetectorRef: { detectChanges: () => void; markForCheck: () => void };
};

Serializer.addClass(
  'infomodal',
  [
    {
      name: 'modalTitle',
      type: 'string',
      default: 'Information',
      category: 'general',
      displayName: 'Modal titel',
    },
    {
      name: 'infoText',
      type: 'text',
      default: '',
      category: 'general',
      displayName: 'Info-tekst (HTML tilladt)',
    },
    {
      name: 'infoPartnerQuestion',
      type: 'string',
      default: '',
      category: 'general',
      displayName: 'Partner-spørgsmål (name)',
    },
  ],
  () => new InfoModalQuestion('infomodal'),
  'question'
);

function syncInfoModalInstance(question: InfoModalQuestion, instance: InfoModalComponentRef['instance']): void {
  instance.modalTitle = question.modalTitle || question.title || 'Information';
  instance.infoText = question.infoText || '';
  const titleLocation = (question as InfoModalQuestion & { titleLocation?: string })
    .titleLocation;
  const hideLabel =
    titleLocation === 'hidden' || titleLocation === 'Hidden';
  instance.label = hideLabel ? '' : question.title || '';
}

export const infoModalQuestion = {
  name: 'infomodal',
  title: 'Info modal',
  iconName: 'icon-info',
  category: 'Custom Questions',
  widgetIsLoaded: () => true,
  isFit: (question: { getType: () => string }) => question.getType() === 'infomodal',
  activatedByChanged: (activatedBy: string) => activatedBy === 'property',
  isDefaultRender: false,
  htmlTemplate: "<div class='dynamic-infomodal-container'></div>",

  afterRender: async (question: InfoModalQuestion, element: HTMLElement) => {
    if (!question || !element) {
      return;
    }

    const container = element.querySelector('.dynamic-infomodal-container') as HTMLElement | null;
    if (!container) {
      return;
    }

    const questionWithRef = question as InfoModalQuestion & { _infoModalComponent?: InfoModalComponentRef };
    const existingRef = questionWithRef._infoModalComponent;
    if (existingRef?.instance) {
      if (container.dataset['infomodalMounted'] === 'true') {
        syncInfoModalInstance(question, existingRef.instance);
        existingRef.changeDetectorRef.markForCheck();
        attachInfomodalBadgeToPartner(question, element);
        return;
      }
      // The question was re-rendered into a fresh container (e.g. after switching pages):
      // the old component lives in a detached DOM node, so drop it and mount a new one.
      try {
        existingRef.instance.isOpen = false;
        const appRef = (window as { surveyAppRef?: { detachView: (v: unknown) => void } }).surveyAppRef;
        appRef?.detachView(existingRef.hostView);
        existingRef.destroy();
      } catch (error) {
        console.error('[InfoModal] Error replacing component:', error);
      }
      questionWithRef._infoModalComponent = undefined;
    }

    if (container.dataset['infomodalMounted'] === 'true') {
      attachInfomodalBadgeToPartner(question, element);
      return;
    }

    try {
      const injector = (window as { surveyInjector?: unknown }).surveyInjector;
      const appRef = (window as { surveyAppRef?: { attachView: (v: unknown) => void; detachView: (v: unknown) => void } }).surveyAppRef;
      const environmentInjector = (window as { surveyEnvInjector?: unknown }).surveyEnvInjector;

      if (!injector || !appRef || !environmentInjector) {
        return;
      }

      const { DynamicInfomodalComponent } = await import('../components/dynamic-infomodal/dynamic-infomodal.component');
      const { createComponent } = await import('@angular/core');

      const componentRef = createComponent(DynamicInfomodalComponent, {
        environmentInjector: environmentInjector as never,
        hostElement: container,
        elementInjector: injector as never,
      }) as InfoModalComponentRef;

      syncInfoModalInstance(question, componentRef.instance);
      container.dataset['infomodalMounted'] = 'true';

      appRef.attachView(componentRef.hostView);
      componentRef.changeDetectorRef.detectChanges();

      (question as InfoModalQuestion & { _infoModalComponent?: InfoModalComponentRef })._infoModalComponent = componentRef;
      attachInfomodalBadgeToPartner(question, element);
    } catch (error) {
      console.error('[InfoModal] Error creating component:', error);
    }
  },

  destroy: (question: InfoModalQuestion & { _infoModalComponent?: InfoModalComponentRef }) => {
    const componentRef = question._infoModalComponent;
    if (!componentRef) {
      return;
    }

    try {
      componentRef.instance.isOpen = false;
      const appRef = (window as { surveyAppRef?: { detachView: (v: unknown) => void } }).surveyAppRef;
      appRef?.detachView(componentRef.hostView);
      componentRef.destroy();
    } catch (error) {
      console.error('[InfoModal] Error destroying component:', error);
    } finally {
      question._infoModalComponent = undefined;
    }
  },
};

CustomWidgetCollection.Instance.add(infoModalQuestion, 'customtype');
